import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@/config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import { useFloorPlanPanelStore } from '@/shared/store/useFloorPlanPanelStore'
import {
  FloorPlanCanvas,
  FloorPlanToolbar,
  FloorPlanZoomBar,
  RoomSwitcher,
  useFloorPlanQuery,
  useFloorPlanSelection
} from '@/features/floor-plan'
import styles from './FloorPlanPlanner.module.scss'

function FloorPlanPlanner() {
  const { t } = useTranslation()
  const { selectedTableIds, select, setSelection, clear } =
    useFloorPlanSelection()

  // Editor panel visibility is toggled from the header (la-sliders-h button).
  const isEditorOpen = useFloorPlanPanelStore(s => s.isOpen)
  const setEditorOpen = useFloorPlanPanelStore(s => s.setIsOpen)

  // Open the editor panel on entry so the user doesn't have to find the toggle.
  useEffect(() => {
    setEditorOpen(true)
  }, [setEditorOpen])

  // Selecting a table slides the editor panel open so its options are visible.
  useEffect(() => {
    if (selectedTableIds.length > 0) setEditorOpen(true)
  }, [selectedTableIds, setEditorOpen])

  // Hydrates from the backend when the API is enabled; no-op otherwise.
  useFloorPlanQuery()

  // Mirror the menu/employees layout: the main tile narrows and a side tile
  // appears when the editor panel is open (toggled from the header). The side
  // column is always rendered (Grid can't map over a falsy child), it's just
  // hidden when closed.
  const mainColumnClassName = isEditorOpen ? 'start-2 col-8' : 'start-2 col-11'
  const editorColumnClassName = isEditorOpen
    ? `${styles.container} start-10 col-3`
    : 'hidden'

  return (
    <Grid>
      <div className={`${styles.container} ${mainColumnClassName}`}>
        <Tile color="black">
          <div className={styles.inner}>
            <p className={`title-bold-medium ${styles.title}`}>
              {t('FLOOR_PLAN.TITLE')}
            </p>
            <p className={`body-medium ${styles.description}`}>
              {t('FLOOR_PLAN.DESCRIPTION')}
            </p>

            {/* Only the room tabs (left) and the zoom/fit bar (right) sit above
                the planner, everything else lives in the editor panel. */}
            <div className={styles.topBar}>
              <RoomSwitcher />
              <FloorPlanZoomBar />
            </div>

            <FloorPlanCanvas
              selectedTableIds={selectedTableIds}
              onSelect={select}
              onSetSelection={setSelection}
              onClear={clear}
            />
          </div>
        </Tile>
      </div>

      <div className={editorColumnClassName}>
        <Tile color="black" title={t('FLOOR_PLAN.TOOLBAR.PANEL_TITLE')}>
          <FloorPlanToolbar
            selectedTableIds={selectedTableIds}
            onClear={clear}
          />
        </Tile>
      </div>
    </Grid>
  )
}

export default FloorPlanPlanner
